import { create } from 'zustand';
import { Timestamp } from '@firebase/firestore';
import { InsuranceChecklist, StructureClaim, StakeholderType } from '../types';
import { checklistsApi, ChecklistProgress, buildCompletedItemEntry } from '../api/checklists';
import { structureClaimsApi } from '../api/structureClaims';
import { getErrorMessage } from '../utils/errorHandler';
import { createPrefixedLogger } from '../utils/logger';
import { withLoading } from './utils/withLoading';

const log = createPrefixedLogger('checklistStore');

// --- Progress-snapshot write coalescing -----------------------------------
// Each structureClaims write fans out through onStructureClaimWritten into
// the per-area claimSummaries doc that EVERY connected map client live-
// subscribes to: one read billed per viewer per write. A user working down
// a 20-item checklist used to emit 20 writes; trailing-debounce them so a
// burst of toggles lands as one. The snapshot only powers map coloring, so
// a few seconds of lag is invisible. Held at module scope (not zustand
// state) — no component renders from it.
const SNAPSHOT_DEBOUNCE_MS = 3000;
let snapshotTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSnapshot: { structureId: string; completed: number; total: number } | null = null;

/**
 * Send any pending progress snapshot immediately. The checklist screen
 * calls this on unmount so navigating away never strands the final state.
 */
export function flushProgressSnapshot(): void {
  if (snapshotTimer) {
    clearTimeout(snapshotTimer);
    snapshotTimer = null;
  }
  const pending = pendingSnapshot;
  pendingSnapshot = null;
  if (!pending) return;
  // Snapshot is best-effort — powers map coloring, not the checklist UI.
  structureClaimsApi
    .updateProgressSnapshot(pending.structureId, {
      completed: pending.completed,
      total: pending.total,
    })
    .catch((err) => log.warn('updateProgressSnapshot failed', err));
}

function queueProgressSnapshot(structureId: string, completed: number, total: number): void {
  // A different structure's snapshot is pending (e.g. fast navigation
  // between checklists): flush it now rather than dropping it.
  if (pendingSnapshot && pendingSnapshot.structureId !== structureId) {
    flushProgressSnapshot();
  }
  pendingSnapshot = { structureId, completed, total };
  if (snapshotTimer) clearTimeout(snapshotTimer);
  snapshotTimer = setTimeout(flushProgressSnapshot, SNAPSHOT_DEBOUNCE_MS);
}
// ---------------------------------------------------------------------------

interface ChecklistState {
  // Data
  checklists: InsuranceChecklist[];
  selectedChecklist: InsuranceChecklist | null;
  structureProgress: ChecklistProgress | null;
  /**
   * `${structureId}_${checklistId}` of the pair `structureProgress` was last
   * fetched for, set even when the fetch found nothing. A model the owner has
   * never ticked has no progress doc at all, so `structureProgress == null`
   * cannot tell "not loaded yet" from "loaded, none exists" — and consumers
   * that guessed "not loaded yet" fell back to the claim's progressSnapshot
   * forever, reporting the PREVIOUS model's numbers against the new model.
   */
  structureProgressKey: string | null;
  // Cached default checklist — it's immutable for a given app version, so we
  // fetch it at most once per session instead of refetching on every structure
  // screen mount.
  defaultChecklist: InsuranceChecklist | null;

  // Loading states
  isLoading: boolean;
  isCompleting: boolean;
  error: string | null;

  // Actions
  fetchChecklists: () => Promise<void>;
  fetchChecklist: (checklistId: string) => Promise<InsuranceChecklist | null>;
  fetchDefaultChecklist: (stakeholderType?: StakeholderType) => Promise<InsuranceChecklist | null>;
  fetchChecklistsFor: (
    stakeholderType: StakeholderType,
    serviceAreaId?: string
  ) => Promise<InsuranceChecklist[]>;
  /** The checklist the property page should show: the claim's model, else
   *  (unclaimed) the insurer link's `linkModelId` when given, else the
   *  stakeholder default. */
  fetchChecklistForStructure: (
    structureId: string,
    claim: StructureClaim | null,
    linkModelId?: string
  ) => Promise<InsuranceChecklist | null>;
  fetchStructureProgress: (structureId: string, checklistId: string) => Promise<void>;
  /**
   * Rewrite the claim's `progressSnapshot` when it disagrees with the model
   * actually resolved for the structure. Nothing else recomputes it on a model
   * change, and it is what the map coloring, the agent client list and the
   * property screen read — so after switching models it advertised the old
   * model's score. No-ops unless the progress for the selected model has
   * loaded, a snapshot already exists, and the numbers really differ.
   * Owner-only: the rules reject the write from anyone else.
   */
  reconcileProgressSnapshot: (
    structureId: string,
    snapshot?: { completed: number; total: number }
  ) => void;
  completeItem: (
    structureId: string,
    checklistId: string,
    itemId: string,
    photoUrl?: string,
    photoPath?: string,
    notes?: string
  ) => Promise<void>;
  uncompleteItem: (structureId: string, checklistId: string, itemId: string) => Promise<void>;
  isItemCompleted: (itemId: string) => boolean;
  getCompletedCount: () => number;
  getTotalCount: () => number;
  /**
   * Resolve the checklist for a structure without touching selection state.
   * Used by components that need checklist data but must not clobber the
   * store's selectedChecklist (e.g. the post-create checklist item picker).
   */
  getChecklistForStructure: (structureId: string) => Promise<InsuranceChecklist | null>;
  /**
   * Resolve the model for the signed-out try-then-save preview, without
   * touching selection state: the insurer link's `?model=` id when it
   * resolves, else the recommended homeowner model (FAIR Plan), else the
   * homeowner default. Mirrors ClaimSheet's auto-select minus
   * `user.defaultInsurer` (there is no user).
   */
  resolvePreviewChecklist: (modelId?: string) => Promise<InsuranceChecklist | null>;
  /**
   * Write a preview's locally-held ticks as real progress after the claim
   * lands: one merged progress write, a claim progress-snapshot update (map
   * pin coloring), and a refresh of the store's structureProgress so the
   * now-signed-in checklist UI shows the ticks without a refetch race.
   */
  applyPreviewTicks: (
    structureId: string,
    checklistId: string,
    itemIds: string[],
    totalCount: number
  ) => Promise<void>;
  clearError: () => void;
  /** Session teardown (signOut): drop the previous identity's checklist
   * selection and progress. The default checklist is kept — it's immutable
   * public data, not tied to a user. */
  reset: () => void;
}

export const useChecklistStore = create<ChecklistState>((set, get) => ({
  checklists: [],
  selectedChecklist: null,
  structureProgress: null,
  structureProgressKey: null,
  defaultChecklist: null,
  isLoading: false,
  isCompleting: false,
  error: null,

  fetchChecklists: async () => {
    await withLoading(set, 'isLoading', async () => {
      const checklists = await checklistsApi.getChecklists();
      set({ checklists });
    });
  },

  fetchChecklist: async (checklistId: string) => {
    return await withLoading(set, 'isLoading', async () => {
      const checklist = await checklistsApi.getChecklist(checklistId);
      if (checklist) set({ selectedChecklist: checklist });
      return checklist;
    });
  },

  fetchDefaultChecklist: async (stakeholderType?: StakeholderType) => {
    // Cache the homeowner default (the most common path) to avoid refetches
    // across structure navigations. Other stakeholder types bypass the cache.
    const { defaultChecklist } = get();
    if (defaultChecklist && (!stakeholderType || stakeholderType === 'homeowner')) {
      set({ selectedChecklist: defaultChecklist });
      return defaultChecklist;
    }
    return await withLoading(set, 'isLoading', async () => {
      const checklist = await checklistsApi.getDefaultChecklist(stakeholderType);
      if (checklist) {
        const next: Partial<ChecklistState> = { selectedChecklist: checklist };
        if (!stakeholderType || stakeholderType === 'homeowner') {
          next.defaultChecklist = checklist;
        }
        set(next as ChecklistState);
      }
      return checklist;
    });
  },

  fetchChecklistsFor: async (stakeholderType, serviceAreaId) => {
    return await withLoading(set, 'isLoading', async () => {
      return await checklistsApi.getChecklistsFor({ stakeholderType, serviceAreaId });
    });
  },

  fetchChecklistForStructure: async (
    structureId: string,
    claim: StructureClaim | null,
    linkModelId?: string
  ) => {
    // Clear state that belongs to a different structure before fetching so
    // no consumer ever sees a mismatched (selectedChecklist, structureProgress)
    // pair during the load window.
    const { structureProgress, selectedChecklist } = get();
    if (structureProgress && structureProgress.structureId !== structureId) {
      set({ structureProgress: null, structureProgressKey: null });
    }
    const wantedId = claim?.insurerChecklistId ?? (claim ? undefined : linkModelId);
    if (selectedChecklist && wantedId && selectedChecklist.id !== wantedId) {
      set({ selectedChecklist: null });
    }

    // Prefer the checklist assigned at claim time; on an unclaimed property
    // an insurer link's model is the visitor's intent (a signed-in reader
    // following a State Farm link saw the FAIR Plan default here, Sept 7
    // 2026); otherwise fall back to the default for the claim's stakeholder
    // type (or the generic default).
    if (wantedId) {
      const checklist = await get().fetchChecklist(wantedId);
      if (checklist) return checklist;
    }
    return get().fetchDefaultChecklist(claim?.stakeholderType);
  },

  fetchStructureProgress: async (structureId: string, checklistId: string) => {
    // Same invariant as above at a finer grain: if the cached progress is
    // for a different (structure, checklist) pair, drop it before we fetch so
    // no render cycle sees the stale pair matched against fresh metadata.
    const { structureProgress } = get();
    if (
      structureProgress &&
      (structureProgress.structureId !== structureId ||
        structureProgress.checklistId !== checklistId)
    ) {
      set({ structureProgress: null, structureProgressKey: null });
    }

    await withLoading(set, 'isLoading', async () => {
      const progress = await checklistsApi.getStructureProgress(structureId, checklistId);
      // Key is set even when `progress` is null — absence is an answer here,
      // not a loading state. See structureProgressKey.
      set({ structureProgress: progress, structureProgressKey: `${structureId}_${checklistId}` });
    });
  },

  reconcileProgressSnapshot: (structureId, snapshot) => {
    const { selectedChecklist, structureProgress, structureProgressKey } = get();
    if (!selectedChecklist) return;
    if (structureProgressKey !== `${structureId}_${selectedChecklist.id}`) return;
    // A claim with NO snapshot means "never started", and that is a state the
    // product uses on purpose: the agent screens read it as "Not started"
    // rather than "0%", and the seeded not-started client in
    // scripts/data-invariants-allowlist.json depends on the field staying
    // absent. Inventing {completed:0,total:n} here changes that meaning and
    // moves the agent filter-chip counts. Correct a wrong snapshot; never
    // create one.
    if (!snapshot) return;
    const completed = structureProgress?.completedItems.length ?? 0;
    const total = selectedChecklist.items.length;
    if (snapshot.completed === completed && snapshot.total === total) return;
    // Best-effort, same as the debounced tick path: this powers map coloring
    // and agent screens, never the checklist UI itself.
    structureClaimsApi
      .updateProgressSnapshot(structureId, { completed, total })
      .catch((err) => log.warn('reconcileProgressSnapshot failed', err));
  },

  completeItem: async (
    structureId: string,
    checklistId: string,
    itemId: string,
    photoUrl?: string,
    photoPath?: string,
    notes?: string
  ) => {
    // Optimistic: flip the UI state first so the checkmark is instant, then
    // send the write. Roll back if the API rejects.
    const { structureProgress, selectedChecklist } = get();
    const previous = structureProgress;
    const completedItems = structureProgress?.completedItems || [];
    const newCompletedItems = completedItems.filter((item) => item.itemId !== itemId);
    newCompletedItems.push(buildCompletedItemEntry(itemId, photoUrl, photoPath, notes));

    set({
      structureProgress: {
        structureId,
        checklistId,
        completedItems: newCompletedItems,
        updatedAt: Timestamp.now(),
      },
      // The first tick on a model creates the progress doc, so the key may
      // still be unset (or point at the previous model) — keep it in step.
      structureProgressKey: `${structureId}_${checklistId}`,
      isCompleting: true,
      error: null,
    });

    try {
      await checklistsApi.completeItem(
        structureId,
        checklistId,
        itemId,
        photoUrl,
        photoPath,
        notes
      );
      const total = selectedChecklist?.items.length ?? 0;
      if (total > 0) {
        queueProgressSnapshot(structureId, newCompletedItems.length, total);
      }
      set({ isCompleting: false });
    } catch (err) {
      set({ structureProgress: previous, isCompleting: false, error: getErrorMessage(err) });
      throw err;
    }
  },

  uncompleteItem: async (structureId: string, checklistId: string, itemId: string) => {
    const { structureProgress, selectedChecklist } = get();
    const previous = structureProgress;
    const newCompletedItems =
      structureProgress?.completedItems.filter((item) => item.itemId !== itemId) ?? [];

    if (structureProgress) {
      set({
        structureProgress: {
          ...structureProgress,
          completedItems: newCompletedItems,
          updatedAt: Timestamp.now(),
        },
        isCompleting: true,
        error: null,
      });
    } else {
      set({ isCompleting: true, error: null });
    }

    try {
      await checklistsApi.uncompleteItem(structureId, checklistId, itemId);
      const total = selectedChecklist?.items.length ?? 0;
      if (total > 0) {
        queueProgressSnapshot(structureId, newCompletedItems.length, total);
      }
      set({ isCompleting: false });
    } catch (err) {
      set({ structureProgress: previous, isCompleting: false, error: getErrorMessage(err) });
      throw err;
    }
  },

  isItemCompleted: (itemId: string) => {
    const { structureProgress } = get();
    return structureProgress?.completedItems.some((item) => item.itemId === itemId) ?? false;
  },

  getCompletedCount: () => {
    const { structureProgress } = get();
    return structureProgress?.completedItems.length ?? 0;
  },

  getTotalCount: () => {
    const { selectedChecklist } = get();
    return selectedChecklist?.items.length ?? 0;
  },

  getChecklistForStructure: async (structureId: string) => {
    return await checklistsApi.getChecklistForStructure(structureId);
  },

  resolvePreviewChecklist: async (modelId?: string) => {
    if (modelId) {
      try {
        const checklist = await checklistsApi.getChecklist(modelId);
        if (checklist) return checklist;
      } catch (err) {
        // Bad or stale model id in the link — fall through to the defaults
        // rather than dead-ending the preview.
        log.warn('resolvePreviewChecklist: model id did not resolve', err);
      }
    }
    const list = await checklistsApi.getChecklistsFor({ stakeholderType: 'homeowner' });
    const recommended = list.find((c) => !c.isDefault && c.isRecommended);
    return recommended ?? list.find((c) => c.isDefault) ?? null;
  },

  applyPreviewTicks: async (structureId, checklistId, itemIds, totalCount) => {
    if (itemIds.length === 0) return;
    const completed = await checklistsApi.seedProgress(structureId, checklistId, itemIds);
    if (totalCount > 0) {
      // Direct write, not queueProgressSnapshot — this runs once right after
      // a claim create, so there is no toggle burst to coalesce.
      structureClaimsApi
        .updateProgressSnapshot(structureId, { completed, total: totalCount })
        .catch((err) => log.warn('updateProgressSnapshot failed', err));
    }
    await get().fetchStructureProgress(structureId, checklistId);
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      checklists: [],
      selectedChecklist: null,
      structureProgress: null,
      structureProgressKey: null,
      isLoading: false,
      isCompleting: false,
      error: null,
    });
  },
}));
